// TelcoTrack - PageHeader Component
// Two rows in the page body. The same title is registered for the shell breadcrumb
// (TopbarPageTitleService).
//
//   Title row:    title (left) · pills (center) · real actions (right)
//   Subtitle row: subtitle (left) · icon filters (right)
//
// Pill groups are `[pageHeaderPills]`, or `[ttPageHeaderPills]` when a hub child
// teleports them into `#pillsSlot` (PageHeaderPillsService). The slot spans the
// title row and is justified to the center, so the group stays on the header
// center line when the title or the actions are wide.
// Icon filters stay on the subtitle row: `[pageHeaderFilters]`,
// `[ttPageHeaderFilters]`, and the data-table cluster (search, column filters,
// Export) parked in `#tableToolsSlot` by PageHeaderTableToolsService.
// Primary section tabs belong in the sidebar (`ttTopbarSectionTabs`).
// Nested sub-tabs (e.g. Srovnání cen › Ceník / Správa ceníku) use `[pageHeaderTabs]`.
//
// Usage:
//   <app-page-header title="Uživatelé" subtitle="Adresář lidí" icon="users">
//     <ng-container pageHeaderActions>
//       <app-button [iconOnly]="true" ariaLabel="Přidat" ...>...</app-button>
//     </ng-container>
//   </app-page-header>

import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { PageHeaderFiltersService } from '../../../../core/services/page-header-filters.service';
import { PageHeaderPillsService } from '../../../../core/services/page-header-pills.service';
import { PageHeaderTableToolsService } from '../../../../core/services/page-header-table-tools.service';
import { TopbarPageTitleService } from '../../../../core/services/topbar-page-title.service';
import type { NavIconId } from '../../../utils/nav-icons';

@Component({
  selector: 'app-page-header',
  template: `
    <div class="tt-page-header tt-page-header--split">
      <header class="tt-page-heading">
        <div class="tt-page-header__title-row">
          <div class="tt-page-header__title-side" data-guide="page-heading">
            <h1 *ngIf="title" class="tt-page-heading__title">{{ title }}</h1>
          </div>
          <div class="tt-page-header__pills" #pillsSlot>
            <ng-content select="[pageHeaderPills]"></ng-content>
          </div>
          <div class="tt-page-header__actions" data-guide="page-actions" data-guide-optional="empty">
            <ng-content select="[pageHeaderActions]"></ng-content>
          </div>
        </div>
        <div class="tt-page-header__subtitle-row">
          <p
            *ngIf="subtitle"
            class="tt-page-heading__subtitle"
            [class.tt-page-heading__subtitle--multiline]="subtitle.includes('\n')"
          >{{ subtitle }}</p>
          <div class="tt-page-header__filter-cluster" data-guide="page-filters" data-guide-optional="empty">
            <div class="tt-page-header__filters" #filtersSlot><ng-content select="[pageHeaderFilters]"></ng-content></div>
            <div class="tt-page-header__table-tools" #tableToolsSlot></div>
          </div>
        </div>
      </header>
      <div class="tt-page-header__tabs">
        <ng-content select="[pageHeaderTabs]"></ng-content>
      </div>
      <ng-content select="[pageHeaderExtra]"></ng-content>
    </div>
  `,
})
export class PageHeaderComponent implements AfterViewInit, OnChanges, OnDestroy {
  private readonly ownerId = Symbol('page-header-title');

  @Input() title = '';
  /** Optional secondary line under the in-page title. */
  @Input() subtitle = '';
  /** Sidebar-matching icon shown next to the title in the global topbar. */
  @Input() icon: NavIconId | '' = '';

  @ViewChild('filtersSlot') private filtersSlot?: ElementRef<HTMLElement>;
  @ViewChild('pillsSlot') private pillsSlot?: ElementRef<HTMLElement>;
  @ViewChild('tableToolsSlot') private tableToolsSlot?: ElementRef<HTMLElement>;
  private slotEl: HTMLElement | null = null;
  private pillsSlotEl: HTMLElement | null = null;
  private toolsSlotEl: HTMLElement | null = null;

  constructor(
    private readonly topbarPageTitle: TopbarPageTitleService,
    private readonly pageHeaderFilters: PageHeaderFiltersService,
    private readonly pageHeaderPills: PageHeaderPillsService,
    private readonly tableTools: PageHeaderTableToolsService,
  ) {}

  ngAfterViewInit(): void {
    this.slotEl = this.filtersSlot?.nativeElement ?? null;
    if (this.slotEl) this.pageHeaderFilters.registerSlot(this.slotEl);
    this.pillsSlotEl = this.pillsSlot?.nativeElement ?? null;
    if (this.pillsSlotEl) this.pageHeaderPills.registerSlot(this.pillsSlotEl);
    this.toolsSlotEl = this.tableToolsSlot?.nativeElement ?? null;
    if (this.toolsSlotEl) this.tableTools.registerSlot(this.toolsSlotEl);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['title'] || changes['icon'] || changes['subtitle']) {
      this.topbarPageTitle.setTitle(this.ownerId, this.title, this.icon, this.subtitle);
    }
  }

  ngOnDestroy(): void {
    if (this.slotEl) this.pageHeaderFilters.unregisterSlot(this.slotEl);
    if (this.pillsSlotEl) this.pageHeaderPills.unregisterSlot(this.pillsSlotEl);
    if (this.toolsSlotEl) this.tableTools.unregisterSlot(this.toolsSlotEl);
    this.slotEl = null;
    this.pillsSlotEl = null;
    this.toolsSlotEl = null;
    this.topbarPageTitle.clearTitle(this.ownerId);
  }
}
